import { useReactiveVar } from '@apollo/client'
import { NotificationCountDisplayCap } from '@deltek/specification-client/core/Constants/misc'
import { Kpc } from '@deltek/specification-client/core/Constants/variants'
import { userNotificationResult } from '@deltek/specification-client/core/GraphQL/ReactiveVars/userNotifications'
import UserNotificationContainer from '@deltek/specification-client/core/Menus/UserNotificationContainer'
import { UserNotification } from '@deltek/specification-client/core/Models/currentUserInfo'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Badge from '@material-ui/core/Badge'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import { useHarmonyIconButtonStyles } from 'shared/hooks/useHarmonyIconButtonStyles'
import BellNavIcon from './BellNavIcon'
import HarmonyPopoverTrigger from './HarmonyPopoverTrigger'

const UNREAD_STATE = 'Unread'

// Tighter padding keeps 2-char content ("9+") inside MUI's 20px minWidth floor, so it stays circular.
const useStyles = makeStyles(() => ({
    badge: {
        padding: '0 4px',
    },
    anchorOriginTopRightRectangle: {
        top: pxToRem(4),
        right: pxToRem(5),
    },
}))

export interface HarmonyNotificationBellProps {
    notificationFilter?: (notification: UserNotification) => boolean
}

/**
 * Harmony's constant-outline bell trigger, replacing the vendor's ResponsiveMenus bell
 * (which swaps to a solid glyph once unread notifications exist). Reuses the vendor's
 * shared notification data/dropdown/clear behavior, so it doesn't need its own poll.
 */
const HarmonyNotificationBell = ({
    notificationFilter,
}: HarmonyNotificationBellProps) => {
    const classes = useHarmonyIconButtonStyles()
    const badgeClasses = useStyles()
    const { notifications } = useReactiveVar(userNotificationResult)

    const unreadCount = React.useMemo(() => {
        const unread = notifications.filter(
            (notification: UserNotification) =>
                notification.notificationState === UNREAD_STATE
        )
        return (notificationFilter ? unread.filter(notificationFilter) : unread)
            .length
    }, [notifications, notificationFilter])

    return (
        <HarmonyPopoverTrigger
            testId="buttonNotifications"
            ariaLabel="Notifications Button"
            disabled={unreadCount === 0}
            icon={
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={NotificationCountDisplayCap}
                    classes={{
                        badge: badgeClasses.badge,
                        anchorOriginTopRightRectangle:
                            badgeClasses.anchorOriginTopRightRectangle,
                    }}
                >
                    <BellNavIcon className={classes.icon} />
                </Badge>
            }
        >
            {({ anchorEl, onClose }) => (
                <UserNotificationContainer
                    userNotifications={notifications}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    variant={Kpc}
                    notificationFilter={notificationFilter}
                />
            )}
        </HarmonyPopoverTrigger>
    )
}

export default HarmonyNotificationBell
